import type { LengthUnit } from '@igus/echain-shared-quantities';
import { Length, LengthUnits, Q as EchainQuantity } from '@igus/echain-shared-quantities';
import { getAssociationProductReferences } from '@libs/product.helper';
import type { AssociationProductFragmentEnhanced, ProductFragmentEnhanced } from '@models/enhanced-fragments';
import type { DistinctVariant } from '@product/VariantSelector/MountingBracketSelector/MountingBracketSelector';
import type { SupportTrayProduct } from '@product/VariantSelector/SupportTraySelector/SupportTraySelector';
import { AssociationType } from './associations';
import type { ProductAttribute, ProductAttributeValueAmountUnit } from './attribute.helper';
import { getAttributeValue } from './attribute.helper';

/**
 * Returns the standard support tray product for a given set of support trays. If there is no standard support tray
 * return the first one instead.
 */
export function getStandardSupportTray(
  supportTrays: AssociationProductFragmentEnhanced[]
): AssociationProductFragmentEnhanced | undefined {
  return (
    supportTrays?.find(
      supportTrayProduct =>
        getAttributeValue<boolean>(supportTrayProduct.attributes?.standard_bool as ProductAttribute<boolean>) === true
    ) || supportTrays?.[0]
  );
}

/**
 * Returns the standard support tray for a given set of grouped support tray products
 */
export const getInitialSupportTray = (productsByAssociation: SupportTrayProduct[]) =>
  getStandardSupportTray(productsByAssociation[0].products);

/**
 * returns the standard mounting variant for a variantProduct
 */
export const getStandardMountingVariant = (distinctVariant: DistinctVariant) => {
  const { mountingVariants } = distinctVariant;
  // If the product is not mounted and has no mounting variants, take the default one
  if (!mountingVariants?.length) {
    return distinctVariant.defaultVariant;
  }

  // Search for the standard mounting variant
  return (
    mountingVariants.find(product =>
      getAttributeValue(product.attributes?.standard_bool as ProductAttribute<boolean>)
    ) ||
    // take the first one, if no standard variant is found
    distinctVariant.mountingVariants[0]
  );
};

/**
 * calculates whether the number of Gliders is even or odd
 */
export const isNumGliederEven = (numGlieder: number | undefined): boolean | undefined => {
  if (numGlieder) {
    return numGlieder % 2 === 0;
  }
  return undefined;
};

/**
 * Returns only unassembled Interior Separation products.
 *
 */
export const getInteriorSeparationAssociationProducts = (
  product: ProductFragmentEnhanced,
  associationProducts: Record<string, AssociationProductFragmentEnhanced>
) =>
  getAssociationProductReferences(product, [AssociationType.SEPARATORS], association => {
    const associationProduct = associationProducts?.[association?.productByProductIdTarget?.id || ''] || null;

    // if no associationProduct is found for given association filter out that association
    if (!associationProduct) {
      return false;
    }

    return !getAttributeValue<boolean>(associationProduct?.attributes?.mounted_bool as ProductAttribute<boolean>);
  })?.map(associationReference => associationProducts[associationReference.id]);

export const akeneoToEchainUnit = (akeneoUnit: string | undefined, fallback: LengthUnit): LengthUnit =>
  akeneoUnit && akeneoUnit in Length ? (akeneoUnit as LengthUnit) : fallback;

export const getEchainLength = (attributeValue?: ProductAttributeValueAmountUnit): Length | undefined => {
  if (attributeValue) {
    return EchainQuantity(attributeValue.amount, akeneoToEchainUnit(attributeValue.unit, LengthUnits.mm));
  }
  return undefined;
};

/**
 * Calculates the amount of Innenaufteilungselemente regarding the settings given by the Interior Separation Configurator.
 * e.g. `Innenaufteilung beginnend im ${startingAtNthKettenglied} / montiert in jedem ${perNthKettenglied}. Kettenglied`
 *
 * TODO try to make use of echain here, see https://github.com/igusdev/echain/blob/main/core/libs/shared-articles-oo/src/lib/ia/IaCountUtil.ts
 *
 * !!!!!!!!!!!!! BEWARE !!!!!!!!!!!!!
 * this will not work for "halbstegige Ketten", "ESkin" or "RolEchain"
 * see: https://igusdev.atlassian.net/browse/COM-4638
 */
export const getInteriorSeparatorCountForNumGlieder = (
  startingAtNthKettenglied: number,
  perNthKettenglied: number,
  numGlieder: number
): number => {
  // TODO check for "halbstegige Ketten", "ESkin" or "RolEchain" (COM-4638)

  const offsetLinks = startingAtNthKettenglied - 1;
  const linksRemaining = Math.max(0, numGlieder - offsetLinks);
  return Math.max(0, Math.ceil(linksRemaining / perNthKettenglied));
};
